import { useCallback, useMemo, useState } from 'react';
import { getSearchParams, handleSearchParams } from '../../../../helpers/handleSearchParams';
import {
  DEFAULT_MEASURES,
  DEFAULT_PRESET,
  DIMENSION_BY_KEY,
  MAX_COL_LEVELS,
  MAX_ROW_LEVELS,
  MEASURE_BY_KEY,
  MONEY_SCALES,
  DEFAULT_MONEY_SCALE,
  ALL_MEASURE_KEYS,
  PRESETS,
  matchPreset,
} from './constants';

const readList = (param, valid) => {
  const raw = getSearchParams(param);
  if (raw === null) return null;
  return raw.split(':').filter((k) => valid[k]);
};

const uniq = (list) => list.filter((k, i) => list.indexOf(k) === i);

/**
 * Configuración de la tabla dinámica: dimensiones en filas y columnas
 * (ordenadas), medidas activas y orden. Vive en la URL para que una vista
 * sea compartible por enlace. Garantiza las invariantes que el back exige
 * (sin dimensión repetida entre filas y columnas, máximo de niveles, al
 * menos una medida), así el usuario nunca ve un 400.
 */
export const useStocksPivotConfig = () => {
  const initial = useMemo(() => {
    const preset = PRESETS.find((p) => p.key === getSearchParams('view')) || DEFAULT_PRESET;
    const rows = readList('rows', DIMENSION_BY_KEY) ?? preset.rows;
    const colsRaw = readList('cols', DIMENSION_BY_KEY) ?? preset.cols;
    const cols = colsRaw.filter((k) => !rows.includes(k));
    const measures = readList('measures', MEASURE_BY_KEY);
    const sortBy = getSearchParams('sortBy');
    const repr = MEASURE_BY_KEY[getSearchParams('repr')] ? getSearchParams('repr') : null;
    const baseMeasures = measures && measures.length ? uniq(measures) : DEFAULT_MEASURES;
    return {
      rows: uniq(rows).slice(0, MAX_ROW_LEVELS),
      cols: uniq(cols).slice(0, MAX_COL_LEVELS),
      // Orden canónico (un enlace viejo puede traer otro). Una vista
      // compartida con `repr` trae su medida a la vista aunque no la liste.
      measures: ALL_MEASURE_KEYS.filter((k) => k === repr || baseMeasures.includes(k)),
      sortBy: sortBy && (sortBy === 'label' || MEASURE_BY_KEY[sortBy]) ? sortBy : 'label',
      sortDir: getSearchParams('sortDir') === 'desc' ? 'desc' : 'asc',
      sortExplicit: Boolean(sortBy && (sortBy === 'label' || MEASURE_BY_KEY[sortBy])),
      moneyScale: MONEY_SCALES.some((x) => x.key === getSearchParams('scale'))
        ? getSearchParams('scale')
        : DEFAULT_MONEY_SCALE,
      repr,
    };
  }, []);

  const [rows, setRows] = useState(initial.rows);
  const [cols, setCols] = useState(initial.cols);
  const [measures, setMeasures] = useState(initial.measures);
  const [sortBy, setSortBy] = useState(initial.sortBy);
  const [sortDir, setSortDir] = useState(initial.sortDir);
  // Sin orden elegido no se manda `sortBy` y el back aplica el suyo: etiqueta,
  // con campañas, años y temporadas de la más reciente a la más antigua.
  // Mandar `label:asc` siempre lo anularía (el back respeta el explícito).
  const [sortExplicit, setSortExplicit] = useState(initial.sortExplicit);
  const [moneyScale, setMoneyScale] = useState(initial.moneyScale);
  // Representación (%): `reprMeasure` es lo elegido en el selector y
  // `reprOn` el interruptor. Solo encendido vive en la URL (`repr=pvf`).
  const [reprMeasure, setReprMeasureState] = useState(initial.repr || 'pvf');
  const [reprOn, setReprOn] = useState(Boolean(initial.repr));

  /** Dimensiones que no están ni en filas ni en columnas. */
  const available = useMemo(
    () => Object.keys(DIMENSION_BY_KEY).filter((k) => !rows.includes(k) && !cols.includes(k)),
    [rows, cols],
  );

  /**
   * Mueve una dimensión a una zona (`rows` | `cols` | `available`) en la
   * posición `index` (al final si se omite). Quita la dimensión de donde
   * estuviera. Respeta el máximo de niveles de la zona destino.
   */
  const moveDimension = useCallback(
    (key, zone, index) => {
      if (!DIMENSION_BY_KEY[key]) return;
      const without = (list) => list.filter((k) => k !== key);
      const insert = (list, max) => {
        const base = without(list);
        if (base.length >= max) return null;
        const at = index === undefined || index > base.length ? base.length : Math.max(0, index);
        return [...base.slice(0, at), key, ...base.slice(at)];
      };
      if (zone === 'rows') {
        const next = insert(rows, MAX_ROW_LEVELS);
        if (!next) return;
        setRows(next);
        setCols((c) => without(c));
      } else if (zone === 'cols') {
        const next = insert(cols, MAX_COL_LEVELS);
        if (!next) return;
        setCols(next);
        setRows((r) => without(r));
      } else {
        setRows((r) => without(r));
        setCols((c) => without(c));
      }
    },
    [rows, cols],
  );

  /** Pone una medida a la vista, en el orden canónico. */
  const showMeasure = useCallback((key) => {
    setMeasures((prev) => (prev.includes(key) ? prev : ALL_MEASURE_KEYS.filter((k) => k === key || prev.includes(k))));
  }, []);

  const toggleMeasure = useCallback(
    (key) => {
      if (!MEASURE_BY_KEY[key]) return;
      // Quitar de la tabla la medida representada apaga la representación:
      // no queda celda donde poner el porcentaje.
      if (reprOn && key === reprMeasure && measures.includes(key) && measures.length > 1) setReprOn(false);
      setMeasures((prev) => {
        if (prev.includes(key)) {
          // Siempre queda al menos una medida activa.
          return prev.length > 1 ? prev.filter((k) => k !== key) : prev;
        }
        // Mantener el orden canónico de MEASURES, no el de clic.
        return ALL_MEASURE_KEYS.filter((k) => k === key || prev.includes(k));
      });
    },
    [reprOn, reprMeasure, measures],
  );

  /** Elegir medida de representación; si está encendida, la trae a la tabla. */
  const setReprMeasure = useCallback(
    (key) => {
      if (!MEASURE_BY_KEY[key]) return;
      setReprMeasureState(key);
      if (reprOn) showMeasure(key);
    },
    [reprOn, showMeasure],
  );

  const toggleRepr = useCallback(() => {
    if (!reprOn) showMeasure(reprMeasure);
    setReprOn((v) => !v);
  }, [reprOn, reprMeasure, showMeasure]);

  const repr = reprOn ? reprMeasure : null;

  const applyPreset = useCallback((presetKey) => {
    const preset = PRESETS.find((p) => p.key === presetKey);
    if (!preset) return;
    setRows(preset.rows);
    setCols(preset.cols);
  }, []);

  const swapAxes = useCallback(() => {
    setRows(cols.slice(0, MAX_ROW_LEVELS));
    setCols(rows.slice(0, MAX_COL_LEVELS));
  }, [rows, cols]);

  /** Click en cabecera: misma clave alterna dirección; otra clave arranca desc (medida) o asc (etiqueta). */
  const handleSort = useCallback(
    (key) => {
      setSortExplicit(true);
      if (sortExplicit && key === sortBy) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return;
      }
      setSortBy(key);
      setSortDir(key === 'label' ? 'asc' : 'desc');
    },
    [sortBy, sortExplicit],
  );

  /** Orden explícito (selector de la banda de pautado). */
  const setSort = useCallback((by, dir) => {
    if (by === 'default') {
      setSortExplicit(false);
      setSortBy('label');
      setSortDir('asc');
      return;
    }
    setSortExplicit(true);
    setSortBy(by === 'label' || MEASURE_BY_KEY[by] ? by : 'label');
    setSortDir(dir === 'desc' ? 'desc' : 'asc');
  }, []);

  const activePreset = useMemo(() => matchPreset(rows, cols), [rows, cols]);

  const persistInUrl = () => {
    handleSearchParams('view', activePreset ? activePreset.key : '');
    // Una lista vacía se escribe como `-`: `handleSearchParams` borra los
    // valores vacíos y, sin el parámetro, al abrir el enlace volvería el
    // pautado por defecto (columnas = Género) en vez de "sin columnas".
    handleSearchParams('rows', rows.join(':') || '-');
    handleSearchParams('cols', cols.join(':') || '-');
    handleSearchParams('measures', measures.join(':'));
    handleSearchParams('sortBy', sortExplicit ? sortBy : '');
    handleSearchParams('sortDir', sortExplicit ? sortDir : '');
    handleSearchParams('scale', moneyScale === DEFAULT_MONEY_SCALE ? '' : moneyScale);
    handleSearchParams('repr', repr || '');
  };

  /** Parámetros de pautado para la API y los exports. */
  const configParams = useMemo(
    () => ({
      rows: rows.join(':'),
      cols: cols.join(':'),
      measures: measures.join(':'),
      ...(sortExplicit ? { sortBy, sortDir } : {}),
    }),
    [rows, cols, measures, sortBy, sortDir, sortExplicit],
  );

  /**
   * Parámetros de la consulta JSON. Pide SIEMPRE todas las medidas: el
   * coste en el back es el mismo (una pasada con sumas condicionales), los
   * KPIs enseñan las cinco aunque la tabla muestre dos, y activar o quitar
   * una medida es instantáneo en vez de relanzar la consulta.
   */
  const queryParams = useMemo(
    () => ({
      rows: rows.join(':'),
      cols: cols.join(':'),
      measures: ALL_MEASURE_KEYS.join(':'),
      ...(sortExplicit ? { sortBy, sortDir } : {}),
    }),
    [rows, cols, sortBy, sortDir, sortExplicit],
  );

  return {
    rows,
    cols,
    measures,
    available,
    sortBy,
    sortDir,
    sortExplicit,
    activePreset,
    moveDimension,
    toggleMeasure,
    applyPreset,
    swapAxes,
    handleSort,
    setSort,
    moneyScale,
    setMoneyScale,
    repr,
    reprOn,
    reprMeasure,
    setReprMeasure,
    toggleRepr,
    configParams,
    queryParams,
    persistInUrl,
  };
};
